import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Loader2, Users } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import KeywordEditor, { DescendantDropWarning } from "./KeywordEditor";
import RelatedResourceCard from "./RelatedResourceCard";
import {
    SCOPE_LABELS,
    SCOPE_TYPES,
    deleteDictionary,
    fetchDictionariesByScopeType,
    fetchDictionary,
    fetchDoctor,
    fetchDoctorMappings,
    fetchDoctorsBySpecialty,
    fetchSpecialtiesByOrg,
    saveDictionary,
} from "./api";
import { isUsableScopeValue, resolveDoctorPreviewContext } from "./doctorPreview";
import { dedupeKeywords, resolveEffectiveKeywords } from "./keywords";

const keywordsOf = (dictionary) => dedupeKeywords(dictionary?.keywords || []);

const sameKeywords = (a, b) =>
    a.length === b.length &&
    a.every((keyword, index) => keyword === b[index]);

const loadRelated = async (scopeType, scopeId, org, specialty) => {
    if (scopeType === SCOPE_TYPES.ORG) {
        const [specialties, specialtyDictionaries] = await Promise.all([
            fetchSpecialtiesByOrg(scopeId),
            fetchDictionariesByScopeType(SCOPE_TYPES.SPECIALTY),
        ]);
        return {
            ancestors: [],
            children: (specialties || []).map((row) => ({
                scopeType: SCOPE_TYPES.SPECIALTY,
                scopeId: row.name,
                org: scopeId,
                keywords: keywordsOf(specialtyDictionaries.get(row.name)),
                subtitle: `${row.doctor_count} doctor${
                    row.doctor_count === 1 ? "" : "s"
                }`,
            })),
            doctors: [],
            mappingChoices: [],
        };
    }

    if (scopeType === SCOPE_TYPES.SPECIALTY) {
        if (!org) {
            return {
                ancestors: [],
                children: [],
                doctors: [],
                mappingChoices: [],
            };
        }
        const [doctors, doctorDictionaries] = await Promise.all([
            fetchDoctorsBySpecialty(scopeId, org),
            fetchDictionariesByScopeType(SCOPE_TYPES.DOCTOR),
        ]);
        const orgDictionary = await fetchDictionary(SCOPE_TYPES.ORG, org);
        return {
            ancestors: [
                {
                    scopeType: SCOPE_TYPES.ORG,
                    scopeId: org,
                    keywords: keywordsOf(orgDictionary),
                },
            ],
            children: [],
            doctors: (doctors || []).map((doctor) => ({
                userId: String(doctor.user_id),
                name: doctor.user_name || String(doctor.user_id),
                email: doctor.user_email,
                keywords: keywordsOf(
                    doctorDictionaries.get(String(doctor.user_id)),
                ),
            })),
            mappingChoices: [],
        };
    }

    const [doctor, mappings] = await Promise.all([
        fetchDoctor(scopeId),
        fetchDoctorMappings(scopeId),
    ]);
    const context = resolveDoctorPreviewContext({
        mappings,
        org,
        specialty,
        doctor,
    });
    const identity = {
        doctorName: doctor?.user_name || null,
        doctorEmail: doctor?.user_email || null,
        previewOrg: context.org,
        previewSpecialty: context.specialty,
        mappingChoices: context.ambiguous ? context.candidates : [],
        children: [],
        doctors: [],
    };

    if (context.ambiguous) {
        return { ...identity, ancestors: [] };
    }

    const [specialtyDictionary, orgDictionary] = await Promise.all([
        isUsableScopeValue(context.specialty)
            ? fetchDictionary(SCOPE_TYPES.SPECIALTY, context.specialty)
            : null,
        isUsableScopeValue(context.org)
            ? fetchDictionary(SCOPE_TYPES.ORG, context.org)
            : null,
    ]);

    return {
        ...identity,
        ancestors: [
            isUsableScopeValue(context.specialty) && {
                scopeType: SCOPE_TYPES.SPECIALTY,
                scopeId: context.specialty,
                org: context.org || undefined,
                keywords: keywordsOf(specialtyDictionary),
            },
            isUsableScopeValue(context.org) && {
                scopeType: SCOPE_TYPES.ORG,
                scopeId: context.org,
                keywords: keywordsOf(orgDictionary),
            },
        ].filter(Boolean),
    };
};

const Breadcrumbs = ({ current, onSearch }) => (
    <nav className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
        <button
            type="button"
            onClick={onSearch}
            className="font-medium hover:text-ktx-purple hover:underline"
        >
            Search
        </button>
        <ChevronRight className="h-3 w-3" aria-hidden />
        <span className="font-semibold text-gray-700">{current}</span>
    </nav>
);

const MappingPicker = ({ choices, onPick }) => (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
        <p className="font-medium">
            This doctor is on more than one customer mapping. Pick the org and
            specialty to preview the merged dictionary.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
            {choices.map((choice) => (
                <button
                    key={`${choice.org}:${choice.specialty}`}
                    type="button"
                    onClick={() => onPick(choice)}
                    className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:border-ktx-purple hover:text-ktx-purple"
                >
                    {choice.org}
                    {choice.specialty ? ` · ${choice.specialty}` : ""}
                </button>
            ))}
        </div>
    </div>
);

const DoctorList = ({ doctors, onFocus }) => (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
            <Users className="h-4 w-4 text-gray-500" aria-hidden />
            <span className="text-sm font-semibold text-gray-900">
                Doctors on this specialty
            </span>
            <span className="text-xs text-gray-500">({doctors.length})</span>
        </div>
        {doctors.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-gray-500">
                No doctors are on this specialty.
            </p>
        ) : (
            <div className="max-h-72 overflow-y-auto">
                {doctors.map((doctor) => (
                    <button
                        key={doctor.userId}
                        type="button"
                        onClick={() => onFocus(doctor)}
                        className="flex w-full items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-purple-50"
                    >
                        <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-gray-900">
                                {doctor.name}
                            </span>
                            {doctor.email && (
                                <span className="block truncate text-xs text-gray-500">
                                    {doctor.email}
                                </span>
                            )}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                            {doctor.keywords.length > 0
                                ? `${doctor.keywords.length} keyword${
                                      doctor.keywords.length === 1 ? "" : "s"
                                  }`
                                : "No dictionary"}
                        </span>
                    </button>
                ))}
            </div>
        )}
    </div>
);

const FocusView = ({ focus, onFocusChange, onBackToSearch }) => {
    const { toast } = useToast();
    const { scopeType, scopeId, org, specialty } = focus;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dictionary, setDictionary] = useState(null);
    const [draft, setDraft] = useState([]);
    const [related, setRelated] = useState({
        ancestors: [],
        children: [],
        doctors: [],
        mappingChoices: [],
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            fetchDictionary(scopeType, scopeId),
            loadRelated(scopeType, scopeId, org, specialty),
        ])
            .then(([own, relatedScopes]) => {
                if (cancelled) return;
                setDictionary(own);
                setDraft(keywordsOf(own));
                setRelated(relatedScopes);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [scopeType, scopeId, org, specialty]);

    const saved = keywordsOf(dictionary);
    const dirty = !sameKeywords(saved, draft);

    const focusScope = useCallback(
        (target) => {
            if (
                dirty &&
                !window.confirm(
                    "You have unsaved keywords. Leave this scope and discard them?",
                )
            ) {
                return;
            }
            onFocusChange(target);
        },
        [dirty, onFocusChange],
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await saveDictionary(
                scopeType,
                scopeId,
                dedupeKeywords(draft),
            );
            const stored = response?.dictionary ?? null;
            setDictionary(stored);
            setDraft(keywordsOf(stored));
            toast({
                title: "Dictionary saved",
                description: `${SCOPE_LABELS[scopeType]} "${scopeId}" now boosts ${
                    keywordsOf(stored).length
                } keywords.`,
            });
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Could not save",
                description: err.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        if (
            !window.confirm(
                `Delete the dictionary for ${SCOPE_LABELS[scopeType]} "${scopeId}"?`,
            )
        ) {
            return;
        }
        setSaving(true);
        try {
            await deleteDictionary(scopeType, scopeId);
            setDictionary(null);
            setDraft([]);
            toast({
                title: "Dictionary deleted",
                description: `${SCOPE_LABELS[scopeType]} "${scopeId}" no longer has its own keywords.`,
            });
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Could not delete",
                description: err.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const ancestorScopes = useMemo(
        () =>
            [...related.ancestors].reverse().map((scope) => ({
                scopeType: scope.scopeType,
                label: `${SCOPE_LABELS[scope.scopeType]} ${scope.scopeId}`,
                keywords: scope.keywords,
            })),
        [related.ancestors],
    );

    const descendantDrops = useMemo(() => {
        if (scopeType === SCOPE_TYPES.ORG) {
            const parent = [
                { label: `${SCOPE_LABELS.ORG} ${scopeId}`, keywords: draft },
            ];
            return related.children
                .map((child) => {
                    const result = resolveEffectiveKeywords([
                        ...parent,
                        {
                            label: `${SCOPE_LABELS.SPECIALTY} ${child.scopeId}`,
                            keywords: child.keywords,
                        },
                    ]);
                    const dropped =
                        result.scopes[result.scopes.length - 1]?.droppedKeywords ||
                        [];
                    return dropped.length
                        ? {
                              label: `${SCOPE_LABELS.SPECIALTY} ${child.scopeId}`,
                              keywords: dropped,
                          }
                        : null;
                })
                .filter(Boolean);
        }

        if (scopeType === SCOPE_TYPES.SPECIALTY) {
            const parent = [
                ...ancestorScopes,
                {
                    label: `${SCOPE_LABELS.SPECIALTY} ${scopeId}`,
                    keywords: draft,
                },
            ];
            return related.doctors
                .map((doctor) => {
                    const result = resolveEffectiveKeywords([
                        ...parent,
                        { label: doctor.name, keywords: doctor.keywords || [] },
                    ]);
                    const dropped =
                        result.scopes[result.scopes.length - 1]?.droppedKeywords ||
                        [];
                    return dropped.length
                        ? { label: doctor.name, keywords: dropped }
                        : null;
                })
                .filter(Boolean);
        }

        return [];
    }, [scopeType, scopeId, draft, related.children, related.doctors, ancestorScopes]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading {SCOPE_LABELS[scopeType].toLowerCase()} dictionary...
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-3">
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                </p>
                <button
                    type="button"
                    onClick={onBackToSearch}
                    className="text-sm font-medium text-ktx-purple hover:underline"
                >
                    Back to search
                </button>
            </div>
        );
    }

    const headerTitle =
        scopeType === SCOPE_TYPES.DOCTOR && related.doctorName
            ? related.doctorName
            : scopeId;

    return (
        <div className="space-y-5">
            <Breadcrumbs
                current={headerTitle}
                onSearch={onBackToSearch}
            />

            <div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ktx-purple">
                        {SCOPE_LABELS[scopeType]}
                    </span>
                    <h2 className="text-xl font-semibold text-gray-900">
                        {headerTitle}
                    </h2>
                </div>
                {scopeType === SCOPE_TYPES.DOCTOR && related.doctorEmail && (
                    <p className="mt-1 text-xs text-gray-500">
                        {related.doctorEmail}
                    </p>
                )}
            </div>

            {related.mappingChoices?.length > 0 && (
                <MappingPicker
                    choices={related.mappingChoices}
                    onPick={(choice) =>
                        focusScope({
                            scopeType: SCOPE_TYPES.DOCTOR,
                            scopeId,
                            org: choice.org,
                            specialty: choice.specialty || undefined,
                        })
                    }
                />
            )}

            {scopeType === SCOPE_TYPES.DOCTOR &&
                !related.mappingChoices?.length &&
                !isUsableScopeValue(related.previewOrg) &&
                !isUsableScopeValue(related.previewSpecialty) && (
                    <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        This doctor has no customer org or specialty mapping, so
                        there is no org or specialty dictionary to preview
                        alongside theirs.
                    </p>
                )}

            <KeywordEditor
                scopeLabel={SCOPE_LABELS[scopeType]}
                keywords={draft}
                onChange={setDraft}
                ancestorScopes={ancestorScopes}
                onSave={handleSave}
                onClear={handleClear}
                saving={saving}
                dirty={dirty}
                savedAt={dictionary?.updated_at || null}
                savedBy={dictionary?.updated_by || null}
                existsUpstream={Boolean(dictionary)}
            />
            <DescendantDropWarning impacts={descendantDrops} />

            {(related.ancestors.length > 0 ||
                related.children.length > 0 ||
                related.doctors.length > 0) && (
                <div className="space-y-3 border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                        Related resources
                    </h3>

                    <div className="grid gap-3 lg:grid-cols-2">
                        {related.ancestors.map((scope) => (
                            <RelatedResourceCard
                                key={`${scope.scopeType}:${scope.scopeId}`}
                                scopeType={scope.scopeType}
                                scopeId={scope.scopeId}
                                keywords={scope.keywords}
                                onEdit={() => focusScope(scope)}
                            />
                        ))}
                        {related.children.map((scope) => (
                            <RelatedResourceCard
                                key={`${scope.scopeType}:${scope.scopeId}`}
                                scopeType={scope.scopeType}
                                scopeId={scope.scopeId}
                                keywords={scope.keywords}
                                subtitle={scope.subtitle}
                                onEdit={() => focusScope(scope)}
                            />
                        ))}
                    </div>

                    {scopeType === SCOPE_TYPES.SPECIALTY && (
                        <DoctorList
                            doctors={related.doctors}
                            onFocus={(doctor) =>
                                focusScope({
                                    scopeType: SCOPE_TYPES.DOCTOR,
                                    scopeId: doctor.userId,
                                    org,
                                    specialty: scopeId,
                                })
                            }
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default FocusView;
